import { Base } from '@studiometa/js-toolkit';
import QuantityInput from './QuantityInput.js';
import VariantRadios from './VariantRadios.js';
import PickupAvailability from './PickupAvailability.js';
import PickupAvailabilityDrawer from './PickupAvailabilityDrawer.js';
import CarrouselReassurence from './CarrouselReassurence.js';

const { subscribe, publish, PUB_SUB_EVENTS } = window;

/**
 * ProductInfo
 */
export default class ProductInfo extends Base {
  cartUpdateUnsubscriber = undefined;

  variantChangeUnsubscriber = undefined;

  /**
   * Component Configuration
   */
  static config = {
    name: 'ProductInfo',
    components: {
      QuantityInput,
      VariantRadios,
      PickupAvailability,
      PickupAvailabilityDrawer,
      CarrouselReassurence,
    },
  };

  /**
   * input
   * @returns {HTMLInputElement|undefined}
   */
  get input() {
    if (this.$children?.QuantityInput?.length <= 0) return;
    return this.$children.QuantityInput[0].$refs.input;
  }

  /**
   * variantSelects
   * @returns {VariantRadios}
   */
  get variantSelects() {
    if (this.$children.VariantRadios.length <= 0) {
      throw new Error('No VariantRadios component found');
    }

    return this.$children.VariantRadios[0];
  }

  /**
   * mounted
   */
  mounted() {
    this.currentVariant = this.$el.querySelector('.product-variant-id');
    this.submitButton = this.$el.querySelector('[type="submit"]');

    this.connectedCallback();
  }

  /**
   * connectedCallback
   */
  connectedCallback() {
    if (!this.input) {
      return;
    }

    this.quantityForm = this.$el.querySelector('.product-form__quantity');

    if (!this.quantityForm) {
      return;
    }
    this.setQuantityBoundries();

    if (
      !this.$el.dataset.originalSection &&
      subscribe &&
      PUB_SUB_EVENTS &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      this.cartUpdateUnsubscriber = subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        this.fetchQuantityRules.bind(this),
      );
    }

    if (subscribe && PUB_SUB_EVENTS && PUB_SUB_EVENTS.variantChange) {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const sectionId = this.$el.dataset.originalSection
          ? this.$el.dataset.originalSection
          : this.$el.dataset.section;
        if (event.data.sectionId !== sectionId) return;
        this.updateQuantityRules(event.data.sectionId, event.data.html);
        this.setQuantityBoundries();
      });
    }
  }

  /**
   * disconnectedCallback
   */
  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
  }

  /**
   * setQuantityBoundries
   */
  setQuantityBoundries() {
    const data = {
      cartQuantity: this.input.dataset.cartQuantity
        ? Number.parseInt(this.input.dataset.cartQuantity, 10)
        : 0,
      min: this.input.dataset.min ? Number.parseInt(this.input.dataset.min, 10) : 1,
      max: this.input.dataset.max ? Number.parseInt(this.input.dataset.max, 10) : null,
      step: this.input.step ? Number.parseInt(this.input.step, 10) : 1,
    };

    let { min } = data;
    const max = data.max === null ? data.max : data.max - data.cartQuantity;
    if (max !== null) min = Math.min(min, max);
    if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

    this.input.min = min;
    this.input.max = max;
    this.input.value = min;

    if (publish && PUB_SUB_EVENTS && PUB_SUB_EVENTS.quantityUpdate) {
      publish(PUB_SUB_EVENTS.quantityUpdate);
    }
  }

  /**
   * fetchQuantityRules
   */
  fetchQuantityRules() {
    if (!this.currentVariant || !this.currentVariant.value) {
      return;
    }

    this.$el.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
    fetch(
      `${this.$el.dataset.url}?variant=${this.currentVariant.value}&section_id=${this.$el.dataset.section}`,
    )
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateQuantityRules(this.$el.dataset.section, html);
        this.setQuantityBoundries();
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.$el.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden');
      });
  }

  /**
   * updateQuantityRules
   * @param {*} sectionId
   * @param {*} html
   */
  updateQuantityRules(sectionId, html) {
    const quantityFormUpdated = html.querySelector(`#Quantity-Form-${sectionId}`);
    const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];

    for (const selector of selectors) {
      const current = this.quantityForm.querySelector(selector);
      const updated = quantityFormUpdated.querySelector(selector);

      if (!current || !updated) {
        continue;
      }

      if (selector === '.quantity__input') {
        const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];

        for (const attribute of attributes) {
          const valueUpdated = updated.getAttribute(attribute);
          if (valueUpdated !== null) current.setAttribute(attribute, valueUpdated);
        }
      } else {
        current.innerHTML = updated.innerHTML;
      }
    }
  }
}
